import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types for booking data
export interface BookingReceipt {
  ReceiptId: string;
  ReferenceNum: string;
  ResponseCode: string;
  ISO: string;
  AuthCode: string;
  TransTime: string;
  TransDate: string;
  TransType: string;
  Complete: string;
  Message: string;
  TransAmount: string;
  CardType: string;
  TransID: string;
  TimedOut: string;
  BankTotals: string;
  Ticket: string;
  CorporateCard: string;
  CvdResultCode: string;
  IsVisaDebit: string;
}

export interface BookingTicket {
  booking: {
    bookingReference: string;
  };
  qrCode:string;
  reservationCode:string;
  price: string;
  status: string;
  passengerType: string;
  isVehicle: boolean;
  vehicleHeight: string | null;
  vehicleLength: string | null;
  vehicleType?: string; // Add vehicle type
  departureTrip: {
    route: {
      departure: string;
      destination: string;
    };
    date: string;
    time: string;
  };
  returnTrip?: {
    route: {
      departure: string;
      destination: string;
    };
    date: string;
    time: string;
  };
}

export interface BookingResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  receipt: BookingReceipt;
  tickets: BookingTicket[];
}

export interface UserFormData {
  firstName?: string;
  lastName?: string;
  // Add other fields as needed
}

export interface BookingState {
  // Current booking response
  bookingResponse: BookingResponse | null;
  
  // User form data from booking process
  userFormData: UserFormData | null;
  
  // Booking status
  isBookingComplete: boolean;
  
  // UI state
  isLoading: boolean;
  error: string | null;
}

const initialState: BookingState = {
  bookingResponse: null,
  userFormData: null,
  isBookingComplete: false,
  isLoading: false,
  error: null
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    // Set booking response after successful booking
    setBookingResponse: (state, action: PayloadAction<BookingResponse>) => {
      state.bookingResponse = action.payload;
      state.isBookingComplete = true;
      state.isLoading = false;
      state.error = null;
    },
    // Set user form data
    setBookingUserData: (state, action: PayloadAction<UserFormData>) => {
      state.userFormData = action.payload;
    },
    
    // Set loading state
    setBookingLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    // Set error
    setBookingError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    
    // Clear booking data (for new booking)
    clearBookingData: (state) => {
      state.bookingResponse = null;
      state.userFormData = null;
      state.isBookingComplete = false;
      state.error = null;
      state.isLoading = false;
    }
  }
});

export const {
  setBookingResponse,
  setBookingUserData,
  setBookingLoading,
  setBookingError,
  clearBookingData
} = bookingSlice.actions;

// Selector function to get passenger name (use this in components)
export const selectPassengerName = (state: { booking: BookingState }) => {
  if (state.booking.userFormData) {
    const fullName = `${state.booking.userFormData.firstName || ''} ${state.booking.userFormData.lastName || ''}`.trim();
    return fullName || 'Guest User';
  }
  return 'Guest User';
};

export default bookingSlice.reducer;